import type {ExtensionAPI} from '@wix/document-manager-core'
import type {CompRef, IdMap, IdMapValue} from '@wix/document-services-types'
import {getNextIdForPrefix} from './aiExtensionContent'

import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import type {ViewerExtensionAPI} from '../viewer/viewerExtension'
import {fileInfoDomain, infoPath, generationTimeBudget} from './constants'

interface MediaPropsInfo {
    idMapValue: IdMapValue
    data: any
    width: number
    height: number
}

type MediaMeasurements = Record<string, {width: number; height: number}>

interface MediaMap {
    idMap: IdMap
    originalData: Record<string, any>
    mediaMeasurements: MediaMeasurements
}

const executionIdToMessageTypeMap: Record<string, (data: any) => void> = {}

export const subscribeWorkFlowHandler = (executionId: string, callback: (data: any) => void) => {
    executionIdToMessageTypeMap[executionId] = data => {
        callback(data)
        delete executionIdToMessageTypeMap[executionId]
    }
}

export const getMediaPropsInfo = (
    extensionAPI: ExtensionAPI,
    componentPointer: CompRef,
    mediaType: string
): MediaPropsInfo[] => {
    const {manifest, viewer} = extensionAPI as ManifestExtensionAPI & InnerElementsExtensionAPI & ViewerExtensionAPI
    const mediaPropsInfo: MediaPropsInfo[] = []
    const mediaProps = manifest.getData(componentPointer, true, [{prop: mediaType}])
    mediaProps.forEach(context => {
        const {prop, path, value: data} = context
        const measurements =
            path !== undefined
                ? viewer.getInnerElementMeasurements(componentPointer, path)
                : viewer.getComponentMeasurements(componentPointer as CompRef)
        mediaPropsInfo.push({
            idMapValue: {pointer: componentPointer, prop, path} as IdMapValue,
            data,
            width: measurements.width,
            height: measurements.height
        })
    })
    return mediaPropsInfo
}

export const createMediaMap = (
    extensionAPI: ExtensionAPI,
    componentPointers: CompRef[],
    mediaType: string
): MediaMap => {
    const typeCounter: Record<string, number> = {}
    const mediaMeasurements: MediaMeasurements = {}
    const idMap: IdMap = {}
    const originalData: Record<string, any> = {}
    for (const componentPointer of componentPointers) {
        const mediaPropsInfo = getMediaPropsInfo(extensionAPI, componentPointer, mediaType)
        for (const mediaPropInfo of mediaPropsInfo) {
            const mappedId = getNextIdForPrefix(mediaType, typeCounter)
            mediaMeasurements[mappedId] = mediaPropInfo
            idMap[mappedId] = mediaPropInfo.idMapValue
            originalData[mappedId] = mediaPropInfo.data
        }
    }

    return {idMap, originalData, mediaMeasurements}
}

export const getMediaInfo = async (serverFacade: any, id: string): Promise<any> => {
    return await serverFacade.fetch(`${fileInfoDomain}/${id}/${infoPath}`, 'GET')
}

export const postForMedia = async <T>(
    serverFacade: any,
    endpoint: string,
    paramsMap: Record<string, any>
): Promise<T> => {
    const {message} = await serverFacade.post(endpoint, paramsMap, {
        'X-Time-Budget': generationTimeBudget
    })
    return JSON.parse(message)
}
